import React from 'react';
import { createPortal } from 'react-dom';
import { Bike, Briefcase, Car, Coins, Flag, Smartphone, Target, X } from 'lucide-react';

const fmtL = (n) => (n >= 10000000 ? `₹${n / 10000000} Cr` : `₹${n / 100000} L`);

export const REWARD_STEPS = [
  { step: 1, target: 2000000, gift: 'Latest iPhone', icon: Smartphone, note: 'Hit ₹20 lakh in total deal value.' },
  { step: 2, target: 5000000, gift: 'Brand-new EV scooty', icon: Bike, note: 'Hit ₹50 lakh in total deal value.' },
  { step: 3, target: 10000000, gift: 'Brand-new SUV + permanent job offer', icon: Car,
    note: 'Hit ₹1 Cr. Your reward is worth 1.5% of the total value (₹1.5 L on ₹1 Cr), plus an offer letter and kit as a permanent sales agent.' },
];

export const PartnerRules = () => (
  <div className="space-y-6" data-testid="partner-rules">
    <div className="grid sm:grid-cols-3 gap-3">
      {[
        { icon: Flag, title: 'Give genuine leads', text: 'Share real property leads — sellers or buyers. Good leads keep you in the program.' },
        { icon: Target, title: 'First-month target', text: 'Start at ₹1 lakh and grow up to ₹20 lakh in deal value in your first month.' },
        { icon: Coins, title: '2.5% commission', text: 'Earn 2.5% from Acre Mitra on every successful deal you bring.' },
      ].map((r, i) => (
        <div key={r.title} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`partner-rule-${i + 1}`}>
          <r.icon size={20} className="text-emerald-700" />
          <div className="mt-2 font-bold text-slate-900">{r.title}</div>
          <p className="text-sm text-slate-600 mt-1">{r.text}</p>
        </div>
      ))}
    </div>
    <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-emerald-900 text-white p-5 sm:p-6" data-testid="partner-reward-ladder">
      <div className="text-[11px] font-black uppercase tracking-widest text-emerald-300 mb-4">Your road to a permanent role</div>
      <ol className="grid sm:grid-cols-3 gap-4">
        {REWARD_STEPS.map((s) => (
          <li key={s.step} className="rounded-xl bg-white/10 border border-white/15 p-4" data-testid={`partner-step-${s.step}`}>
            <div className="flex items-center gap-2">
              <span className="h-7 w-7 rounded-full bg-amber-400 text-slate-900 grid place-items-center text-xs font-black">{s.step}</span>
              <span className="font-black text-lg">{fmtL(s.target)}</span>
            </div>
            <div className="mt-3 flex items-center gap-2 text-amber-200 font-bold"><s.icon size={18} /> {s.gift}</div>
            <p className="text-xs text-slate-300 mt-2 leading-relaxed">{s.note}</p>
          </li>
        ))}
      </ol>
      <p className="mt-4 text-xs text-slate-300 flex items-start gap-2"><Briefcase size={14} className="shrink-0 mt-0.5" />
        Reach ₹1 Cr in total revenue and you become a permanent Acre Mitra sales agent, with an offer letter and joining kit.</p>
    </div>
  </div>
);

export const PrivacyPolicyModal = ({ open, onClose }) => {
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[1000] bg-slate-900/60 grid place-items-center p-4" data-testid="partner-privacy-modal">
      <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
        <button type="button" onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-700" data-testid="partner-privacy-close"><X size={18} /></button>
        <h3 className="text-2xl font-black text-slate-900">Privacy Policy · Channel Partners</h3>
        <div className="mt-4 space-y-3 text-sm text-slate-700 leading-relaxed">
          <p><b>1. What we collect.</b> Your name, photo, phone, email, address, licence or RERA number, and the property or customer leads you share with us.</p>
          <p><b>2. Why we use it.</b> To verify you, issue your agent number and ID card, pay commissions and rewards, and contact you about deals.</p>
          <p><b>3. Leads you share.</b> Only share details of people who have agreed to be contacted. Acre Mitra uses them only to serve that customer.</p>
          <p><b>4. Sharing.</b> We never sell your data. It is shared only with banks, legal or payment partners when a deal needs it, or when the law requires it.</p>
          <p><b>5. Storage & security.</b> Data is stored on secured servers, and only authorised Acre Mitra staff can see it.</p>
          <p><b>6. Your rights.</b> You can ask to see, correct or delete your data by writing to us. Deleting it ends your partnership.</p>
          <p><b>7. Conduct.</b> Fake leads, misuse of the Acre Mitra name or sharing customer data outside the program ends the partnership immediately.</p>
        </div>
        <button type="button" onClick={onClose} className="btn-am-primary mt-6 w-full py-3" data-testid="partner-privacy-accept">I have read the policy</button>
      </div>
    </div>,
    document.body,
  );
};
